export type HeatmapMetric = "salesUnits" | "salesAmount";

export const itl1Regions = [
    "North East",
    "North West",
    "Yorkshire and the Humber",
    "East Midlands",
    "West Midlands",
    "East of England",
    "London",
    "South East",
    "South West",
    "Wales",
    "Scotland",
    "Northern Ireland"
] as const;

export type Itl1Region = (typeof itl1Regions)[number];

const itl1RegionNames = new Set<string>(itl1Regions);

export interface HeatmapStore {
    storeNumber: string;
    storeName: string;
    salesUnits: number | null;
    salesAmount: number | null;
    latitude: number | null;
    longitude: number | null;
    region: string | null;
}

export interface PlottedStore {
    storeNumber: string;
    storeName: string;
    latitude: number;
    longitude: number;
    region: string | null;
    value: number;
}

export interface PlottedStores {
    plotted: PlottedStore[];
    unlocatedCount: number;
}

export function normaliseStoreNumber(storeNumber: string): string {
    const stripped = storeNumber.trim().replace(/^0+/, "");
    return stripped.length === 0 ? "0" : stripped;
}

export function storeHeatmapValue(store: HeatmapStore, metric: HeatmapMetric): number | null {
    const value = metric === "salesUnits" ? store.salesUnits : store.salesAmount;
    if (value == null || !Number.isFinite(value) || value === 0) {
        return null;
    }

    return value;
}

function hasCoordinates(store: HeatmapStore): store is HeatmapStore & { latitude: number; longitude: number } {
    return store.latitude != null
        && store.longitude != null
        && Number.isFinite(store.latitude)
        && Number.isFinite(store.longitude);
}

export function plotSainsburysStores(
    stores: readonly HeatmapStore[],
    metric: HeatmapMetric
): PlottedStores {
    const plotted: PlottedStore[] = [];
    let unlocatedCount = 0;

    for (const store of stores) {
        const value = storeHeatmapValue(store, metric);
        if (value == null) {
            continue;
        }

        if (!hasCoordinates(store)) {
            unlocatedCount += 1;
            continue;
        }

        plotted.push({
            storeNumber: store.storeNumber,
            storeName: store.storeName,
            latitude: store.latitude,
            longitude: store.longitude,
            region: store.region,
            value
        });
    }

    plotted.sort((left, right) => left.value - right.value);
    return { plotted, unlocatedCount };
}

export interface RegionSales {
    region: Itl1Region;
    total: number;
    storeCount: number;
}

function isItl1Region(region: string | null): region is Itl1Region {
    return region != null && itl1RegionNames.has(region);
}

export function summariseItl1Regions(plotted: readonly PlottedStore[]): { regions: RegionSales[]; unassignedCount: number } {
    const totals = new Map<Itl1Region, RegionSales>(
        itl1Regions.map(region => [region, { region, total: 0, storeCount: 0 }])
    );
    let unassignedCount = 0;

    for (const store of plotted) {
        if (!isItl1Region(store.region)) {
            unassignedCount += 1;
            continue;
        }

        const current = totals.get(store.region);
        if (!current) {
            unassignedCount += 1;
            continue;
        }

        current.total += store.value;
        current.storeCount += 1;
    }

    return {
        regions: Array.from(totals.values()).sort((left, right) => right.total - left.total || left.region.localeCompare(right.region)),
        unassignedCount
    };
}

export function markerRadius(value: number, maxValue: number): number {
    if (maxValue <= 0) {
        return 6;
    }

    const share = Math.min(1, Math.max(0, value / maxValue));
    return 5 + share * 13;
}

export function markerColor(value: number, maxValue: number): string {
    const share = maxValue <= 0 ? 0 : Math.min(1, Math.max(0, value / maxValue));
    const hue = Math.round(48 - share * 48);
    const lightness = Math.round(58 - share * 18);
    return `hsl(${hue}, 85%, ${lightness}%)`;
}
